/**
 * TODO: Migration notes — remove after app-layer Input/InputDecorator usages are migrated to TextInput.
 *
 * This component replaces the app-layer Input + InputDecorator pair. The following
 * app-layer APIs were intentionally dropped and need handling during migration:
 *
 * - hideFromFullstory (51 usages) — pass className="fs-mask" directly.
 * - faIcon (14 usages, mostly "search") — use prefix={<SearchMd />} with Untitled UI icons.
 * - isLockedFeature (handful on InputDecorator) — app-level concern, wrap externally.
 * - inputType="number" (6 usages) — use className/style to adjust padding if browser
 *   spinners are visible, or hide them with appearance: textfield.
 * - variant="h2" (1 usage, ReportDetailsHeader) — use className for one-off font overrides.
 * - isClearable+hideClearButton (13 usages) — replaced by onClear presence.
 * - inline on InputDecorator (1 usage, DropdownBuilder) — use className to override padding.
 * - InputLabel required indicator (50+ form fields) — Form.Label needs a `required` prop
 *   added; this is the biggest gap.
 * - InputHelpText (9 usages, all UserEditor) — use <Text.Label size="xsmall" color="subtle">.
 * - InputValidation (6 usages) — use invalid + suffix={<WarningIcon />} + Form.Error.
 * - variant="asText" (1 usage) — covered by variant="plain".
 * - variant="asTransparent" (0 usages) — dead code, no migration needed.
 */

import { XClose } from '@timelyapp/icons/solid-icons';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import React from 'react';
import styles from './TextInput.module.scss';

type Merge<M, N> = Omit<M, Extract<keyof M, keyof N>> & N;

const cvaWrapper = cva(styles.wrapper, {
  variants: {
    size: {
      small: styles['size-small'],
      medium: styles['size-medium'],
      large: styles['size-large'],
    },
    variant: {
      default: styles['variant-default'],
      plain: styles['variant-plain'],
      search: styles['variant-search'],
    },
  },
  defaultVariants: { size: 'medium', variant: 'default' },
});

export type TextInputSize = NonNullable<VariantProps<typeof cvaWrapper>['size']>;
export type TextInputVariant = NonNullable<VariantProps<typeof cvaWrapper>['variant']>;

export type TextInputProps = Merge<
  React.InputHTMLAttributes<HTMLInputElement>,
  VariantProps<typeof cvaWrapper> & {
    /** Content rendered before the input (icon, currency symbol, etc.) */
    prefix?: React.ReactNode;
    /** Content rendered after the input (unit label, icon, etc.) */
    suffix?: React.ReactNode;
    /** If provided, a clear (X) button is shown. Called when user clicks it. */
    onClear?: (event: React.MouseEvent<HTMLButtonElement>) => void;
    /** Visual invalid state (red border). */
    invalid?: boolean;
    /** Select all text in the input when it receives focus. */
    selectOnFocus?: boolean;
  }
>;

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>((props, ref) => {
  const {
    className,
    size,
    variant,
    prefix,
    suffix,
    onClear,
    invalid,
    selectOnFocus,
    onFocus,
    type = 'text',
    ...inputProps
  } = props;

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (selectOnFocus) {
      event.target.select();
    }
    onFocus?.(event);
  };

  const hasPrefix = !!prefix;
  const hasSuffix = !!suffix;
  const hasClear = !!onClear;

  return (
    <div
      className={cvaWrapper({ size, variant, className })}
      data-invalid={invalid || undefined}
      data-has-prefix={hasPrefix || undefined}
      data-has-suffix={hasSuffix || undefined}
      data-has-clear={hasClear || undefined}
    >
      {hasPrefix && (
        <span className={styles.prefix} aria-hidden="true">
          {prefix}
        </span>
      )}
      <input
        ref={ref}
        type={type}
        className={styles.input}
        onFocus={selectOnFocus ? handleFocus : onFocus}
        aria-invalid={invalid || undefined}
        {...inputProps}
      />
      {hasSuffix && (
        <span className={styles.suffix} aria-hidden="true">
          {suffix}
        </span>
      )}
      {hasClear && (
        <button
          type="button"
          className={styles.clearButton}
          onClick={onClear}
          aria-label="Clear input"
          tabIndex={-1}
        >
          <XClose />
        </button>
      )}
    </div>
  );
});

TextInput.displayName = 'TextInput';
